import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, BehaviorSubject, throwError } from "rxjs";

import { AuthenticationService } from "../services/auth.service";
import { ObjectResultDto } from "../models/ObjectResult";
import { catchError, switchMap, filter, take } from "rxjs/operators";
import { Router } from "@angular/router";

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // add authorization header with jwt token if available
    const currentUser = this.authenticationService.currentUser();
    if (currentUser && currentUser.access_token) {
      request = this.addToken(request, currentUser.access_token);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        } else {
          return throwError(error);
        }
      }),
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authenticationService.loginWithRefreshToken().pipe(
        switchMap((response: ObjectResultDto) => {
          this.isRefreshing = false;
          if (response.success) {
            this.refreshTokenSubject.next(response.data.access_token);
            let nextCall = this.addToken(req, response.data.access_token);
            return next.handle(nextCall);
          } else {
            this.router.navigate(["/account/login"]);
          }
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(req, jwt));
        }),
      );
    }
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const r = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        "ngrok-skip-browser-warning": "true", // This is to avoid the ngrok browser warning and allow the request to be made
      },
    });

    return r;
  }
}
